import { z } from 'zod';
import { appLogQuery, monthNumber, yearNumber } from '@overseer/contracts';
import { defaultYearMonth } from '@/lib/remembered-month';
import { currentYearMonth } from '@/lib/time';

/**
 * URL search parameters, validated.
 *
 * The URL is the state, so it is also untrusted input: someone can type
 * `?month=13` or arrive with a link from an older build. Parsing with a schema
 * and falling back to a sane default means a malformed URL renders a usable
 * month rather than an error page or an empty grid.
 *
 * Kept out of `router.tsx` so the screen can import the type without pulling the
 * route tree in with it.
 */

/**
 * Year and month when the URL does not name them.
 *
 * A named month in the query string still wins — a link to June is a link to
 * June. When the key is missing (the nav item, a name on the grid) or unusable
 * (`?month=13`), this tab's last month is used, then the current Warsaw month.
 * See `remembered-month.ts` for why that is sessionStorage and not localStorage.
 */
const yearFromUrl = z.coerce
  .number()
  .pipe(yearNumber)
  .catch(() => defaultYearMonth().year)
  .default(() => defaultYearMonth().year);

const monthFromUrl = z.coerce
  .number()
  .pipe(monthNumber)
  .catch(() => defaultYearMonth().month)
  .default(() => defaultYearMonth().month);

/**
 * The employee detail's month strip.
 *
 * In the URL rather than in component state, for the same reason the grid's month
 * is (§6.1): "look at Kowalski's January" should be a link. It also means the
 * strip survives a reload after a correction, which is exactly when somebody is
 * looking at it.
 *
 * `.default()` as well as `.catch()`, which is the trap the timesheet schema
 * below records and this hit again immediately: `.catch()` makes a bad value
 * parse, but the key stays *required in the input type*, so every
 * `<Link to="/hr/employees/$employeeId">` in the app has to name a month it does
 * not care about. The employee list has two such links and both failed to
 * compile the moment this schema existed.
 */
export const employeeDetailSearchSchema = z.object({
  year: yearFromUrl,
  month: monthFromUrl,
});
export type EmployeeDetailSearch = z.infer<typeof employeeDetailSearchSchema>;

export const timesheetSearchSchema = z.object({
  year: yearFromUrl,
  month: monthFromUrl,
  // `.default()` as well as `.catch()`, for the reason recorded on the list
  // schemas below: without a default the key is *required* on the way in, so
  // every `<Link to="/hr/timesheet" search={…}>` has to spell out the whole object —
  // including the two fields it does not care about. Found by a link from the
  // card-read screen that wanted only a month and a work day.
  mode: z.enum(['hours', 'costs']).default('hours').catch('hours'),
  // `.catch()` earns its keep here: `name` was a valid value until the sort was
  // split into surname and forename, so a bookmark or a pasted `?sort=name`
  // still resolves — to `lastName`, which is what that option claimed to do.
  sort: z
    .enum(['lastName', 'firstName', 'warnings', 'hours'])
    .default('lastName')
    .catch('lastName'),
  /**
   * A name search over the grid, the same one the employee list has.
   *
   * `undefined` rather than `''` when empty, so clearing the box leaves the URL
   * without the key instead of with `?q=` — which is the same query and a
   * different cache entry.
   */
  q: z.string().max(255).optional().catch(undefined),
  /**
   * Whether this person's hours are recorded at all.
   *
   * **Defaults to `true`, so the screen opens filtered.** Somebody whose hours
   * nothing records has a month of blanks by construction — showing them adds
   * rows that look like a fault and are not. The default is safe because the
   * filter counts as active: the Clear button appears, so the screen says it is
   * hiding something rather than quietly doing it.
   *
   * Replaces `hasCard`, which asked the same question one step removed — a card
   * was only ever how attendance gets recorded.
   */
  countsHours: z.enum(['true', 'false', 'all']).default('true').catch('true'),
  companyId: z.coerce.number().int().positive().optional().catch(undefined),
  departmentId: z.coerce.number().int().positive().optional().catch(undefined),
  positionId: z.coerce.number().int().positive().optional().catch(undefined),
  /**
   * A work day to open the correction panel on.
   *
   * In the URL so a day can be linked to — the card-read screen points at the day
   * behind a scan, and "look at this day" becomes a link rather than an
   * instruction to click the right cell. Clicking a cell does not write it: that
   * would put a history entry in the back button for every cell opened.
   */
  workDay: z.coerce.number().int().positive().optional().catch(undefined),
});

export type TimesheetSearch = z.infer<typeof timesheetSearchSchema>;

/**
 * List-screen state: which page, which order, which filters.
 *
 * In the URL for the same reason the timesheet's month is: a colleague asking
 * "who is in Catering with no current agreement" should be able to paste a link.
 *
 * `.catch()` on every field, so a hand-edited or stale URL degrades to the default
 * rather than to an error page.
 */
const page = z.coerce.number().int().min(1).default(1).catch(1);
const optionalId = z.coerce.number().int().positive().optional().catch(undefined);

/**
 * Tri-state: only, excluded, or don't care. See `activeFilter` in the contracts.
 *
 * `.default()` as well as `.catch()`, and the pair matters for more than parsing:
 * a schema whose *input* has required keys makes `search` a required prop on every
 * `<Link>` to the route, so `<Link to="/hr/employees">` in a breadcrumb stops
 * compiling. `.default()` makes the key optional on the way in; `.catch()` still
 * salvages a malformed one.
 */
const activeFilter = z.enum(['true', 'false', 'all']).default('true').catch('true');

/** A calendar date in the URL. Anything else degrades to absent, not to an error. */
const isoDateParam = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

export const referenceSearchSchema = z.object({
  page,
  sort: z.enum(['name', '-name', 'id', '-id']).default('name').catch('name'),
  q: z.string().max(255).optional().catch(undefined),
});
export type ReferenceSearchParams = z.infer<typeof referenceSearchSchema>;

export const employeesSearchSchema = z.object({
  page,
  sort: z
    .enum(['lastName', '-lastName', 'firstName', '-firstName', 'id', '-id'])
    .default('lastName')
    .catch('lastName'),
  q: z.string().max(255).optional().catch(undefined),
  isActive: activeFilter,
  companyId: optionalId,
  departmentId: optionalId,
  positionId: optionalId,
});
export type EmployeesSearchParams = z.infer<typeof employeesSearchSchema>;

export const daysOffSearchSchema = z.object({
  page,
  sort: z
    .enum(['startDate', '-startDate', 'employeeName', '-employeeName'])
    .default('-startDate')
    .catch('-startDate'),
  // Both or neither: a year without a month would filter nothing, so the screen
  // treats them as one control. Absent means every absence on record.
  year: z.coerce.number().pipe(yearNumber).optional().catch(undefined),
  month: z.coerce.number().pipe(monthNumber).optional().catch(undefined),
  employeeId: optionalId,
  type: z.enum(['holiday', 'sick', 'care']).optional().catch(undefined),
});
export type DaysOffSearchParams = z.infer<typeof daysOffSearchSchema>;

/**
 * The card-read list.
 *
 * `from`/`to` are a range rather than a single exact date: a reader that stopped
 * reporting is found by looking at a week. Both optional and independent, so
 * "everything since Monday" is expressible.
 */
export const workLogsSearchSchema = z.object({
  page,
  sort: z.enum(['-time', 'time', 'employeeName', '-employeeName']).default('-time').catch('-time'),
  employeeId: optionalId,
  // `0` is a real value here — it selects the hand-entered reads, which carry the
  // sentinel `device_id = 0`. So this cannot use `optionalId`, which requires
  // positive.
  deviceId: z.coerce.number().int().min(0).optional().catch(undefined),
  from: isoDateParam,
  to: isoDateParam,
  includeDeleted: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((value) => value === true || value === 'true')
    .default(false)
    .catch(false),
});
export type WorkLogsSearchParams = z.infer<typeof workLogsSearchSchema>;

export const devicesSearchSchema = z.object({
  page,
  sort: z
    .enum(['locationName', '-locationName', 'lastSynced', '-lastSynced'])
    .default('locationName')
    .catch('locationName'),
  q: z.string().max(255).optional().catch(undefined),
  isActive: activeFilter,
});
export type DevicesSearchParams = z.infer<typeof devicesSearchSchema>;

/**
 * The payroll month.
 *
 * The same two fields as the timesheet's, and the same reasons: the month on
 * screen is a link somebody sends ("check June's payroll"), and a hand-edited or
 * stale `?month=13` degrades to this tab's month rather than to an error page.
 *
 * `.default()` alongside `.catch()` — the trap the timesheet schema records. The
 * cross-links between the two screens both carry a year and a month, but the nav
 * item does not, and without the defaults `<Link to="/hr/payroll">` would not
 * compile.
 */
export const payrollSearchSchema = z.object({
  year: yearFromUrl,
  month: monthFromUrl,

  /**
   * The payroll row whose payslip is open, by entry id.
   *
   * In the URL rather than in component state for two reasons. "Look at Kowalska's
   * June payslip" should be a link, the same argument the month itself is here for.
   * And the screen refetches after every edit — an expansion held in state would
   * survive that, but not the reload somebody does when a figure looks wrong, which
   * is exactly the moment they are reading the derivation.
   *
   * **Optional, not defaulted**, unlike the month: no row open is the normal state
   * and the commonest URL should not carry a parameter saying so. `.catch(undefined)`
   * so a stale bookmark carrying the old `?detail=true`, or an id from a month that
   * no longer has it, collapses to nothing open rather than to an error page.
   *
   * One at a time — see the note on `Line` in `payroll-table.tsx`. A single value
   * rather than a list is what makes that enforceable rather than merely intended.
   */
  expanded: z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .catch(() => undefined),

  /**
   * Narrow the grid to people whose name contains this.
   *
   * Matched in the browser against the rows already loaded, unlike the employee
   * list's `q` which the API applies. The payroll month arrives whole — the
   * subtotals and the summary are computed from it — so there is nothing to fetch
   * for a narrower view, and a round trip per keystroke would refetch a response
   * the screen already holds.
   */
  q: z.string().max(255).optional().catch(undefined),

  /**
   * Narrow to one company, by id.
   *
   * A payroll row is keyed `(month, employee, form, company)`, so a person with
   * agreements at two companies has a row under each and this picks between them
   * rather than between people.
   */
  companyId: z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .catch(() => undefined),

  /**
   * How the rows are ordered inside each contract group.
   *
   * A leading `-` is descending, which is the convention `devicesSearchSchema`
   * already uses here. Defaulted rather than optional: the grid has an order
   * whether or not the URL says so, and naming it means the control shows what is
   * actually in effect instead of a blank.
   *
   * `lastName` is the default because it is what the API sorts by — so the
   * commonest URL carries no sort parameter and still describes the screen truly.
   *
   * The sort is **within a group**, never across one. The bands and their
   * subtotals are the screen's structure; a sort that reordered rows past a
   * subtotal would put figures under a heading that does not total them.
   */
  sort: z
    .enum(['lastName', '-lastName', 'firstName', '-firstName', 'total', '-total', 'ready', '-ready'])
    .default('lastName')
    .catch('lastName'),
});
export type PayrollSearch = z.infer<typeof payrollSearchSchema>;

/**
 * The employee cost reports.
 *
 * The year defaults to the current Warsaw year, not the tab's remembered month:
 * the reports compare years, and an old payroll month left in the tab should not
 * open them on a past year.
 *
 * `month` is optional, not defaulted — the screen picks the last closed month once
 * the report arrives, which the URL cannot know. `range` is the month alone or
 * the year so far through it.
 */
export const reportsSearchSchema = z.object({
  year: z.coerce
    .number()
    .pipe(yearNumber)
    .catch(() => currentYearMonth().year)
    .default(() => currentYearMonth().year),
  month: z.coerce.number().pipe(monthNumber).optional().catch(undefined),
  /** One month, or the year so far through it. */
  range: z.enum(['month', 'ytd']).catch('month').default('month'),
});
export type ReportsSearch = z.infer<typeof reportsSearchSchema>;

/**
 * The old address of cash moves, `/cashier/transfers?desk=`: the desk is
 * carried to where the moves are done now. Absent or unusable, dropped.
 */
export const cashierTransfersSearchSchema = z.object({
  desk: optionalId,
});

/**
 * Kasa główna. `drawer` opens one from a link: the cash to accept
 * (`handovers`) or the payroll's cash pay (`payouts`). Anything else is
 * dropped, and the page opens without one.
 */
export const cashierMainDrawer = z.enum(['handovers', 'payouts']);
export type CashierMainDrawer = z.infer<typeof cashierMainDrawer>;

export const cashierMainSearchSchema = z.object({
  drawer: cashierMainDrawer.optional().catch(undefined),
});

/**
 * Administracja → Logi. Every filter the API takes except the page cursor, so
 * "the 500s around 14:02" or "every line of this request" is a link. Each key
 * is the contract's own rule; a value it refuses is dropped.
 */
export const logsSearchSchema = z.object({
  level: appLogQuery.shape.level.catch(undefined),
  status: appLogQuery.shape.status.catch(undefined),
  q: appLogQuery.shape.q.catch(undefined),
  requestId: appLogQuery.shape.requestId.catch(undefined),
  to: appLogQuery.shape.to.catch(undefined),
});
export type LogsSearch = z.infer<typeof logsSearchSchema>;
